import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle,
  faExclamationTriangle,
  faInfoCircle,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import clsx from 'clsx';

interface FloatingAlertProps {
  type: 'success' | 'error' | 'warning' | 'info';
  title?: string;
  message: string;
  isVisible: boolean;
  onDismiss: () => void;
  autoHideDuration?: number;
}

const iconMap = {
  success: faCheckCircle,
  error: faExclamationTriangle,
  warning: faExclamationTriangle,
  info: faInfoCircle,
};

const colorMap = {
  success: {
    bg: 'bg-green-50',
    border: 'border-green-200',
    icon: 'text-green-600',
    title: 'text-green-900',
    message: 'text-green-800',
    button: 'bg-green-100 text-green-800 hover:bg-green-200',
  },
  error: {
    bg: 'bg-red-50',
    border: 'border-red-200',
    icon: 'text-red-600',
    title: 'text-red-900',
    message: 'text-red-800',
    button: 'bg-red-100 text-red-800 hover:bg-red-200',
  },
  warning: {
    bg: 'bg-yellow-50',
    border: 'border-yellow-200',
    icon: 'text-yellow-600',
    title: 'text-yellow-900',
    message: 'text-yellow-800',
    button: 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200',
  },
  info: {
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    icon: 'text-blue-600',
    title: 'text-blue-900',
    message: 'text-blue-800',
    button: 'bg-blue-100 text-blue-800 hover:bg-blue-200',
  },
};

export default function FloatingAlert({
  type,
  title,
  message,
  isVisible,
  onDismiss,
  autoHideDuration = 5000,
}: FloatingAlertProps) {
  useEffect(() => {
    if (isVisible && autoHideDuration > 0) {
      const timer = setTimeout(() => {
        onDismiss();
      }, autoHideDuration);
      return () => clearTimeout(timer);
    }
  }, [isVisible, autoHideDuration, onDismiss]);

  const colors = colorMap[type];
  const Icon = iconMap[type];

  return createPortal(
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onDismiss}
          className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[9999] p-3 sm:p-4"
        >
          {/* Alert */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: -20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: -20 }}
            transition={{ duration: 0.2 }}
            onClick={(e) => e.stopPropagation()}
            className={clsx(
              'w-full max-w-[calc(100vw-2rem)] sm:max-w-md',
              'rounded-xl border-2 shadow-2xl',
              'relative overflow-hidden',
              colors.bg,
              colors.border
            )}
          >
            <div className="p-4 sm:p-6">
              <div className="flex items-start gap-3">
                <div className={clsx('flex-shrink-0', colors.icon)}>
                  <FontAwesomeIcon icon={Icon} className="w-5 h-5 sm:w-6 sm:h-6" aria-hidden="true" />
                </div>
                <div className="flex-1 min-w-0" style={{ overflowWrap: 'break-word', wordBreak: 'break-word' }}>
                  {title && (
                    <h3 className={clsx('text-sm sm:text-base font-semibold mb-1.5 sm:mb-2', colors.title)}>
                      {title}
                    </h3>
                  )}
                  <p className={clsx('text-xs sm:text-sm leading-relaxed', colors.message)} style={{ overflowWrap: 'break-word', wordBreak: 'break-word' }}>{message}</p>
                </div>
                <button
                  onClick={onDismiss}
                  className={clsx(
                    'flex-shrink-0 rounded-full p-1.5 sm:p-2 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 min-w-[32px] min-h-[32px] flex items-center justify-center',
                    colors.button,
                    type === 'success' ? 'focus:ring-green-500' :
                    type === 'error' ? 'focus:ring-red-500' :
                    type === 'warning' ? 'focus:ring-yellow-500' :
                    'focus:ring-blue-500'
                  )}
                  aria-label="Dismiss alert"
                >
                  <FontAwesomeIcon icon={faTimes} className="w-3 h-3 sm:w-4 sm:h-4" />
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}

